/**
 * useOperations Hook
 *
 * React Query hooks for fetching operations and connectors from Azure APIs.
 * Based on LogicAppsUX browse.ts query patterns.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { useDebounce } from './useDebounce';
import {
  fetchBuiltInOperations,
  discoveryOperationToDefinition,
  getClientBuiltInOperations,
  fetchAllAzureConnectors,
  fetchOperationsForAzureConnector,
  fetchFuzzySearchOperations,
  DiscoveryOperation,
} from '@/services/operationsService';
import { setDiscoveryHints } from '@/services/operationManifestService';
import type { OperationDefinition } from '@/types/operations';
import { isLocalMode } from '@/services/localRuntimeClient';
import { isArmMockMode } from '@/services/armConfig';

/**
 * Re-rank search results using Fuse.js for better relevance
 * Uses weighted field matching similar to LogicAppsUX:
 * - name (display name): highest weight (2.0)
 * - connectorName: high weight (1.8)
 * - description: medium weight (1.5)
 * - id: lower weight for exact operationId matches (1.2)
 */
const reRankSearchResults = (
  operations: OperationDefinition[],
  searchTerm: string,
  limit: number = 50
): OperationDefinition[] => {
  if (!searchTerm.trim() || operations.length === 0) {
    return operations;
  }

  // Configure Fuse.js with weighted fields
  const fuse = new Fuse(operations, {
    keys: [
      { name: 'name', weight: 2.0 },
      { name: 'connectorName', weight: 1.8 },
      { name: 'description', weight: 1.5 },
      { name: 'id', weight: 1.2 },
    ],
    threshold: 0.4, // 0 = exact match, 1 = match anything
    includeScore: true,
    ignoreLocation: true, // Don't penalize matches that appear later in the string
    minMatchCharLength: 2,
  });

  // Perform fuzzy search
  const results = fuse.search(searchTerm);

  // Return re-ranked results with scores, limited to top matches
  return results.slice(0, limit).map((result) => ({
    ...result.item,
    searchScore: result.score ?? 1,
  }));
};

// Query cache options
const queryOpts = {
  staleTime: 1000 * 60 * 60 * 8, // 8 hours
  gcTime: 1000 * 60 * 60 * 8, // 8 hours
};

/**
 * Hook to fetch built-in operations from the Logic App runtime API
 */
export const useBuiltInOperationsQuery = () => {
  const { getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();

  return useQuery({
    queryKey: ['operations', 'builtin', selectedProject?.toLowerCase(), selectedApp?.name.toLowerCase()],
    queryFn: async () => {
      if (!selectedProject || !selectedApp) return [];

      const token = await getOttoToken();
      if (!token) return [];

      try {
        return await fetchBuiltInOperations({
          projectName: selectedProject,
          appName: selectedApp.name,
          accessToken: token,
        });
      } catch (error) {
        console.warn('Failed to fetch built-in operations from API (using client-side fallback):', error);
        return [];
      }
    },
    enabled: !!selectedProject && !!selectedApp,
    retry: false, // Don't retry on 404
    ...queryOpts,
  });
};

/**
 * Hook to search operations using backend fuzzy search + client-side re-ranking
 *
 * Two-stage search:
 * 1. Backend: Azure's fuzzy search API fetches candidate operations
 * 2. Client: Fuse.js re-ranks results with weighted field matching for better relevance
 *
 * This matches LogicAppsUX behavior where search prioritizes:
 * - Operation name (display name)
 * - Connector name
 * - Description
 *
 * Includes 300ms debounce to avoid excessive API calls
 */
export const useSearchOperations = (searchTerm: string, category?: 'trigger' | 'action' | 'all') => {
  const { getOttoToken } = useAuth();
  const { selectedProject } = useAppStore();

  // Debounce search term to avoid excessive API calls (300ms delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Get preloaded runtime built-in operations (HTTP, Service Bus, SQL, etc.)
  // These are fetched by usePreloadOperations and cached by React Query
  const { data: runtimeBuiltIns } = useBuiltInOperationsQuery();

  // Determine action type for filtering
  const actionType = category === 'trigger' ? ('trigger' as const) : category === 'action' ? ('action' as const) : undefined;

  const query = useQuery({
    queryKey: ['searchOperations', debouncedSearchTerm, category, selectedProject?.toLowerCase(), runtimeBuiltIns?.length ?? 0],
    queryFn: async () => {
      if (!debouncedSearchTerm.trim()) return [];

      const accessToken = await getOttoToken();
      if (!accessToken && !isArmMockMode()) return [];

      // Stage 1: Use backend fuzzy search for Azure managed connector operations
      let operations: DiscoveryOperation[] = [];
      try {
        operations = await fetchFuzzySearchOperations(
          {
            projectName: selectedProject ?? '',
            accessToken: accessToken ?? '',
          },
          debouncedSearchTerm,
          actionType
        );
      } catch {
        // API may fail in local mode — fall through to client-side operations
      }

      // Convert Azure API results to OperationDefinition format
      const converted = operations.map(discoveryOperationToDefinition);

      // Merge all built-in operation sources:
      // 1. Client-side hardcoded ops (Control, Variables, HTTP, etc.) — always available
      // 2. Runtime API ops (Service Bus, SQL, Cosmos DB, Azure Blob, etc.) — from preloaded query
      const mergedMap = new Map<string, OperationDefinition>();

      // Add client-side built-in operations first (fallback/baseline)
      const clientOps = getClientBuiltInOperations()
        .map(discoveryOperationToDefinition)
        .filter((op) => !actionType || (actionType === 'trigger' ? op.category === 'trigger' : op.category !== 'trigger'));
      clientOps.forEach((op) => mergedMap.set(op.id, op));

      // Add runtime built-in operations (overrides client-side with richer data from API)
      if (runtimeBuiltIns && runtimeBuiltIns.length > 0) {
        const runtimeOps = runtimeBuiltIns
          .map(discoveryOperationToDefinition)
          .filter((op) => (!actionType || (actionType === 'trigger' ? op.category === 'trigger' : op.category !== 'trigger')) && op.type.toLowerCase() !== 'compose');
        runtimeOps.forEach((op) => mergedMap.set(op.id, op));
      }

      // Add Azure managed connector results (these take final precedence)
      converted.forEach((op) => mergedMap.set(op.id, op));
      let merged = Array.from(mergedMap.values());

      // Hide conversational agent triggers (A2A Request, On Message)
      merged = merged.filter((op) => op.kind !== 'Agent' || op.category !== 'trigger');

      // Stage 2: Re-rank results using Fuse.js with weighted field matching
      // This filters out loosely matching results and prioritizes exact matches
      return reRankSearchResults(merged, debouncedSearchTerm, 500);
    },
    enabled: !!selectedProject && !!debouncedSearchTerm.trim(),
    ...queryOpts,
  });

  // Return empty array when no search term instead of loading
  const data = debouncedSearchTerm.trim() ? (query.data || []) : [];
  // Show loading if user is still typing (searchTerm !== debouncedSearchTerm) or query is loading
  const isLoading = (searchTerm.trim() && searchTerm !== debouncedSearchTerm) || (debouncedSearchTerm.trim() ? query.isLoading : false);

  return { data, isLoading, error: query.error };
};

/**
 * Hook to get built-in operations grouped by connector
 */
export const useOperationsGroupedByConnector = (category?: 'trigger' | 'action' | 'all') => {
  const { data: apiOperations, isLoading: apiLoading, error: apiError, isFetching } = useBuiltInOperationsQuery();

  const groupedData = useMemo(() => {
    // Start with client-side built-in operations (always available)
    const clientOperations = getClientBuiltInOperations();

    // Merge with API operations, preferring API results but including client-side fallbacks
    const operationsMap = new Map<string, OperationDefinition>();

    // Add client-side operations first
    clientOperations.forEach((op) => {
      operationsMap.set(op.id, discoveryOperationToDefinition(op));
    });

    // Override/add with API operations if available
    if (apiOperations && apiOperations.length > 0) {
      apiOperations
        .filter(op => op.type?.toLowerCase() !== 'compose')
        .forEach((op) => {
        operationsMap.set(op.id, discoveryOperationToDefinition(op));
      });
    }

    let operations = Array.from(operationsMap.values());

    // Filter by category
    if (category === 'trigger') {
      operations = operations.filter((op) => op.category === 'trigger');
    } else if (category === 'action') {
      operations = operations.filter((op) => op.category !== 'trigger');
    }

    // Hide conversational agent triggers (A2A Request, On Message) —
    // conversational agent-kind workflows are not supported in Otto yet.
    operations = operations.filter((op) => op.kind !== 'Agent' || op.category !== 'trigger');

    // Group by connector
    const groups: Record<string, { connector: string; operations: OperationDefinition[] }> = {};

    for (const op of operations) {
      const connectorKey = op.connectorName || op.type || 'Other';
      if (!groups[connectorKey]) {
        groups[connectorKey] = { connector: connectorKey, operations: [] };
      }
      groups[connectorKey].operations.push(op);
    }

    return Object.values(groups);
  }, [apiOperations, category]);

  // Only show loading if we're actually fetching data
  const isLoading = apiLoading && isFetching;

  return { data: groupedData, isLoading, error: apiError };
};

/**
 * Hook: Azure managed connectors (operation groups)
 * In local mode, returns empty array since Azure connectors require data plane authentication
 * and real Azure connections that aren't available locally.
 */
export const useAzureConnectorGroups = () => {
  const { getOttoToken } = useAuth();
  const { selectedApp } = useAppStore();

  return useQuery({
    queryKey: ['connectorGroups', 'azure', selectedApp?.projectName?.toLowerCase()],
    // Disable in local mode unless mock ARM service is available
    enabled: !!selectedApp?.projectName && (!isLocalMode() || isArmMockMode()),
    queryFn: async () => {
      if (!selectedApp?.projectName) return [];
      
      // Skip if local mode without mock ARM
      if (isLocalMode() && !isArmMockMode()) {
        console.log('[useAzureConnectorGroups] Skipping Azure connector fetch in local mode (no mock ARM)');
        return [];
      }

      const accessToken = await getOttoToken();
      if (!accessToken && !isArmMockMode()) return [];

      const result = await fetchAllAzureConnectors(
        {
          projectName: selectedApp.projectName,
          accessToken: accessToken ?? '',
        },
      );

      return result;
    },
    ...queryOpts,
  });
};

/**
 * Hook: Operations for an Azure connector (lazy-loaded)
 * This is used when user expands an Azure connector to see its operations
 * In local mode, returns empty array since Azure connectors aren't available.
 */
export const useOperationsByConnector = (
  connectorId: string | null,
  actionType?: 'triggers' | 'actions'
) => {
  const { getOttoToken } = useAuth();
  const { selectedApp } = useAppStore();

  const projectName = useMemo(() => selectedApp?.projectName, [selectedApp]);
  return useQuery({
    queryKey: ['operationsByConnector', connectorId, actionType, projectName?.toLowerCase()],
    // Disable in local mode unless mock ARM service is available
    enabled: !!projectName && !!connectorId && (!isLocalMode() || isArmMockMode()),
    queryFn: async () => {
      if (!projectName || !connectorId) return [];
      
      // Skip if local mode without mock ARM
      if (isLocalMode() && !isArmMockMode()) {
        console.log('[useOperationsByConnector] Skipping Azure connector operations in local mode (no mock ARM)');
        return [];
      }

      const accessToken = await getOttoToken();
      if (!accessToken && !isArmMockMode()) return [];

      const connectorName = connectorId.split('/').splice(-1)[0] ?? '';
      // Fetch operations from Azure managed APIs
      const operations = await fetchOperationsForAzureConnector(
        {
          projectName,
          accessToken: accessToken ?? '',
        },
        connectorName,
        actionType,
        'picker-drillin',
      );

      return operations.map(discoveryOperationToDefinition);
    },
    ...queryOpts,
  });
};

/**
 * Preloader hook - call this in a parent component to start fetching operations/connectors early
 * This preloads the data that's actually used in the operation picker:
 * - Built-in operations (grouped by connector)
 * - Azure managed connector groups
 */
export const usePreloadOperations = () => {
  // Preload built-in operations (used by useOperationsGroupedByConnector)
  const { data: builtInOps } = useBuiltInOperationsQuery();

  // Feed discovery hints into the manifest service when built-in operations arrive.
  // This replaces the duplicate fetchBuiltInOperations call that was in useRuntimeManifestSync.
  useEffect(() => {
    if (builtInOps && builtInOps.length > 0) {
      setDiscoveryHints(builtInOps as Parameters<typeof setDiscoveryHints>[0]);
    }
  }, [builtInOps]);

  // Preload Azure connector groups (used by operation picker for "Action in an app")
  useAzureConnectorGroups();
};
